import { useEffect, useState, useCallback, useMemo, useRef, useContext } from "react";
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { Box, Button, Container, Typography } from "@mui/material";
import { GridToolbarColumnsButton, GridToolbarExportContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';

import { Style } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { callPostApi } from "../../function/ApiCallFunction";
import GradientLoader from "../../components/CommanComponents/GradientLoader";

import CampaignAddModal from "../../components/modal/CampaignAddModal";
import CustomFooterWithLoadMore from "../../include/footerWithLoadMore";
import { DataContext } from "../../context/DataContext";

const CashiSaleReport = () => 
{
    const [allData,        setAllData]        = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [pageIndex,      setPageIndex]      = useState(0);
    const [hasMoreData,    setHasMoreData]    = useState(true);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [filtersLoaded,  setFiltersLoaded]  = useState(false);

    const [open, setOpen] = useState(false);
    
    const isInitialSessionLoad = useRef(false);
    const hasFetchedRef        = useRef(false);
    const navigate = useNavigate();

    const location = useLocation();
    const { startdate, enddate } = location.state || {};

    /* ------------------ USER ------------------ */
    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id;

    /* ------------------ DATE HELPER ------------------ */
    // Default Filter For Searching
    const defaultFilters = useMemo(() => ({store_id, startDate:startdate, endDate:enddate, fetchLimit: '10', pageSize: '10' }), [store_id,startdate,enddate]);
    const [filters, setFilters] = useState(defaultFilters);

    // Reset Data States
    const resetDataStates = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setLoading(false);
    };

    // Apply Filters
    const applyFilters = () => 
    {
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        setFiltersApplied(true);
    };

    // Clear Filters
    const clearFilters = () => 
    {
        setFilters(defaultFilters);
        setAllData([]);
        setPageIndex(0);
        setHasMoreData(true);
        sessionStorage.setItem('cashiSaleReportFilter', JSON.stringify(defaultFilters));
        setFiltersApplied(true);
    };

    // Load from sessionStorage
    useEffect(() => 
    {
        const storedState = sessionStorage.getItem('cashiSaleReportFilter');
        if (storedState) 
        {
            try 
            {
                const parsedState = JSON.parse(storedState);
                setFilters({ ...defaultFilters, ...parsedState });
                isInitialSessionLoad.current = true;
                setFiltersApplied(true);
            } 
            catch (error) 
            {               
                sessionStorage.removeItem('cashiSaleReportFilter');
            }
        }
    }, [defaultFilters]);

    // Fetch Data Function
    const fetchData = useCallback(async (page = pageIndex) => 
    {
        if (!filtersLoaded || loading || !hasMoreData) return;

        setLoading(true);
        try 
        {
            const StartNum = page * Number(filters.fetchLimit);

            const payload = { mod: "CASHI_SALE_REPORT", data_arr: {...filters,start: StartNum,limit: filters.fetchLimit,}};
            
            const response = await callPostApi("store-cashi-report", payload);
            if (response.status === '200' && response.data) 
            {
                const apiResponse = response.data;

                if (apiResponse.length > 0) 
                {
                    setAllData(prev => [...prev, ...apiResponse]);
                    setPageIndex(page);
                } 
                else {  setHasMoreData(false); }
                
                if (apiResponse.length < filters.fetchLimit) { setHasMoreData(false); }
            } 
            else  { setHasMoreData(false); }
        } 
        catch (error) { setHasMoreData(false); } 
        finally { setLoading(false); }

    }, [filters, filtersLoaded, loading, hasMoreData, pageIndex]);

    // Initial fetch trigger
    useEffect(() => {
        if (!hasFetchedRef.current && filtersLoaded) {
            hasFetchedRef.current = true;
            fetchData();
        }
    }, [filtersLoaded, fetchData]);

    // Save to sessionStorage
    useEffect(() => {
        if (filtersApplied) 
        {
            sessionStorage.setItem('cashiSaleReportFilter', JSON.stringify(filters));
            if (!isInitialSessionLoad.current) { fetchData(0); }
            isInitialSessionLoad.current = false;
            setFiltersApplied(false);
        }
    }, [filtersApplied, filters, fetchData]);

    // Set filters loaded once
    useEffect(() => { if (!filtersLoaded) setFiltersLoaded(true); }, [filtersLoaded]);

    
    const CustomToolbar = () => {
        return (
            <GridToolbarExportContainer sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                <GridToolbarColumnsButton /><GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarExportContainer>
        );
    };

    const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];

    return (
    <div>
    <Container maxWidth="xl" sx={{ my: 2 }}>
  
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>

        {/* Left side */}
        <Button variant="contained" sx={{ background: "#1976d2", mr: 1 }} onClick={() => navigate(-1)}>Back</Button>
        <Button variant="contained" sx={{ background: "#1d4ed8", textTransform: "none" }} onClick={() => navigate("/registerSale")}>Register sale</Button>

        {/* Right side */}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>

            <Button sx={{ background: "linear-gradient(195deg, #42424a, #191919)", color: "#ffffff", boxShadow:
                    "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",}}onClick={clearFilters}>Clear Filters</Button>
        </Box>

    </Box>

      {/* Title */}
      <Typography ariant="h6"  gutterBottom sx={{ background: "linear-gradient(195deg, #49a3f1, #1A73E8)",  p: 1, color: "#ffffff",
          borderRadius: "4px", mb: 3, display: "flex", alignItems: "center",}}><Style sx={{ mr: 1 }} /> Total Sale
      </Typography>

      <Box sx={{ mb: 2, display: "flex", justifyContent: "end" }}> 
        <Box> </Box>
      </Box>

      <div className='mb-3'>
          <div className="row align-items-center g-2 searchFilter">

              <div className="col-12 col-md-2">
                  <input type="date" className="form-control" placeholder="Start Date" value={filters.startDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} />
              </div>

              <div className="col-12 col-md-2">
                  <input type="date" className="form-control" placeholder="End Date" value={filters.endDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}  />
              </div>

              {/* Page Size Filter */}  
              <div className="col-12 col-md-2">
                  <select className="form-control" value={filters.fetchLimit} onChange={(e) => setFilters(prev => ({ ...prev, fetchLimit: e.target.value }))} >
                      <option value="">Record Fetch Limit</option>
                      {fetchLimits.map(value => ( <option key={value} value={value}>{value}</option>))}
                  </select>
              </div>  

              {/* Apply flter */}
              <div className="col-12 col-md-2 ms-auto">                                        
                  <Button  sx={{background:'linear-gradient(195deg, #49a3f1, #1A73E8)', color:'#ffffff',ml:1,
                      boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px' }}
                          style={{float: 'right'}}  onClick={() => { resetDataStates(); fetchData(0); applyFilters(); }} >Apply Filters</Button>
              </div>  
              
          </div>
      </div>

      {/* DataGrid */}
        <Box sx={{ height: 'auto', width: '100%' }}>
            <IndiaDataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
             columns={[
              { field: 'srNo',            headerName: 'Sr. No.',        flex: 0.5, sortable: false, filterable: false,},              
              { field: "order_amount",    headerName: "Order Amount",   flex: 1.5 },
              { field: "customer_name",   headerName: "Customer Name",  flex: 1.5 },
              { field: "date_added",      headerName: "Date Added",     flex: 1.5 },      
              { field: "payment_type",    headerName: "Payment Type",   flex: 1.5 },
              { field: "customer_mobile", headerName: "Mobile",         flex: 0.8,
                  renderCell: (params) => 
                  { 
                      let customer_mobile = params.row.customer_mobile;

                      if (!customer_mobile) return '-';

                      const mobileStr = customer_mobile.toString();
                      customer_mobile = `********${mobileStr.slice(-4)}`;

                      return <span>{customer_mobile}</span>;
                  } 
              },       
            ]} 
            
            localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
            pageSize={Number(filters.pageSize)}
            onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
            pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
            pagination
            
            disableSelectionOnClick 
            
            initialState={{pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},}}
            slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} hasMoreData={hasMoreData} loading={loading} /> ), }}
            slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
            sx={{ '& .MuiDataGrid-cell': {fontSize: '13px', }, 
                  '& .MuiDataGrid-columnHeaderTitle': {fontSize: '15px',fontWeight: 'bold', whiteSpace: 'normal !important',lineHeight: '1.5', textAlign: 'center',},
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"} }}
            rowHeight={45} headerHeight={45}
        />
      </Box>
      
      <CampaignAddModal open={open} onClose={() => setOpen(false)} />    {/* Add Campaign Dialog */}
      {loading && <GradientLoader text="Loading" />}       { /* Loader */}
    </Container>

    </div>
  );

}

export default CashiSaleReport;